import { z } from "zod";

/**
 * Shared DTO cho Feature Flags (US-AE2.F6-0008 / @UC9013 · E14) — một nguồn sự thật cho
 * api (S02 — guard + validate biên PATCH kill-switch/rollout) và admin web (S04 — render/sửa).
 *
 * Khớp đúng Prisma model FeatureFlag (feature_flags):
 *  - key: string (unique)
 *  - enabled: boolean (kill-switch master)
 *  - rolloutPercent: int [0,100]
 *  - scope: string | null  (cohort/stage filter; null = all)
 *  - stage: string | null
 *
 * BẢO MẬT / invariants:
 * - DTO chỉ mang trạng thái flag — KHÔNG secret/token (C2 redaction là việc của audit diff).
 * - PATCH body `.strict()` (chặn key lạ) + `.refine` (ít nhất 1 field) — fail-closed tại biên.
 * - rolloutPercent siết int [0,100] tại biên (C5) — defense-in-depth ngoài CHECK DB.
 */

export const ROLLOUT_PERCENT_MIN = 0;
export const ROLLOUT_PERCENT_MAX = 100;

/** Biên độ dài scope/stage để chặn payload phình (DoS biên) + giữ nhãn ngắn gọn. */
export const FEATURE_FLAG_LABEL_MAX = 128;

/** View read-only của một flag (khớp FeatureFlagView service + Prisma FeatureFlag). */
export const featureFlagSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  rolloutPercent: z.number().int().min(ROLLOUT_PERCENT_MIN).max(ROLLOUT_PERCENT_MAX),
  scope: z.string().nullable(),
  stage: z.string().nullable(),
});

export type FeatureFlagDto = z.infer<typeof featureFlagSchema>;

/**
 * Kết quả EVALUATE một flag cho một subject (US-AE7.F6-0160 S01, @UC9168).
 *
 * `on` = flag có bật cho subject này không (enabled × scope × rollout-bucket).
 * `reason` = VÌ SAO — để observability/audit truy vết quyết định kill-switch, KHÔNG đoán.
 * Rollout bucket dùng hash ổn định (FNV-1a) trên `key+subjectId` ⇒ cùng subject luôn cùng kết quả.
 */
export const FLAG_EVAL_REASON = {
  /** enabled=false ⇒ kill-switch master tắt, chặn tất cả. */
  KILL_SWITCH: "kill_switch",
  /** scope != null và subject không thuộc cohort. */
  SCOPE_MISS: "scope_miss",
  /** bucket(subject) >= rolloutPercent ⇒ ngoài vùng rollout. */
  ROLLOUT_OUT: "rollout_out",
  /** bật: enabled + scope khớp + bucket < rolloutPercent. */
  ON: "on",
} as const;

export type FlagEvalReason = (typeof FLAG_EVAL_REASON)[keyof typeof FLAG_EVAL_REASON];

export const flagEvaluationSchema = z.object({
  key: z.string(),
  on: z.boolean(),
  reason: z.enum([
    FLAG_EVAL_REASON.KILL_SWITCH,
    FLAG_EVAL_REASON.SCOPE_MISS,
    FLAG_EVAL_REASON.ROLLOUT_OUT,
    FLAG_EVAL_REASON.ON,
  ]),
});

export type FlagEvaluation = z.infer<typeof flagEvaluationSchema>;

/**
 * Body của PATCH /v1/admin/feature-flags/:key (kill-switch/rollout edit @UC9013).
 *
 * 4 field OPTIONAL nhưng `.refine` ép ít nhất một field hiện diện (chặn body rỗng → 400).
 * `.strict()` để loại key ngoài contract (fail-closed input · C5). rolloutPercent int [0,100].
 * scope/stage nullable (set null = bỏ filter, áp dụng toàn bộ).
 */
export const patchFeatureFlagSchema = z
  .object({
    enabled: z.boolean().optional(),
    rolloutPercent: z
      .number()
      .int()
      .min(ROLLOUT_PERCENT_MIN)
      .max(ROLLOUT_PERCENT_MAX)
      .optional(),
    scope: z.string().max(FEATURE_FLAG_LABEL_MAX).nullable().optional(),
    stage: z.string().max(FEATURE_FLAG_LABEL_MAX).nullable().optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.enabled !== undefined ||
      body.rolloutPercent !== undefined ||
      body.scope !== undefined ||
      body.stage !== undefined,
    { message: "At least one field is required" },
  );

export type PatchFeatureFlagRequest = z.infer<typeof patchFeatureFlagSchema>;
